import React, { useState, useRef, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    dropdownContainer: {
        position: 'relative',
        width: '100%'
    },
    dropdownTrigger: {
        width: '100%',
        border: '0px',
        fontFamily: 'Manrope',
        fontSize: '1.75rem',
        fontWeight: 500,
        color: '#1F2051',
        backgroundColor: 'transparent',
        resize: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '&:focus': {
            outline: 'none !important'
        }
    },
    dropdownText: {
        '&.placeholder': {
            fontStyle: 'italic',
            opacity: 0.6
        }
    },
    chevronIcon: {
        width: '20px',
        height: '20px',
        transition: 'transform 200ms ease',
        '&.open': {
            transform: 'rotate(180deg)'
        }
    },
    dropdownMenu: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: '8px',
        backgroundColor: '#fff',
        border: '1px solid #E3E4FA',
        borderRadius: '10px',
        boxShadow: '0px 8px 16px 4px rgba(0, 0, 0, 0.2)',
        zIndex: 50,
        maxHeight: '320px',
        overflowY: 'auto'
    },
    menuItem: {
        padding: '16px 20px',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        fontFamily: 'Manrope',
        fontSize: '1.4rem',
        fontWeight: 500,
        color: '#1F2051',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
            backgroundColor: '#F8F9FA'
        },
        '&.selected': {
            backgroundColor: '#E3E4FA80'
        },
        '&.disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: 'transparent'
        }
    },
    menuItemSubtitle: {
        opacity: 0.6,
        fontSize: '0.8rem',
        color: '#1F2051',
        fontFamily: 'Manrope'
    }
}));

interface DropdownOption {
    value: string;
    label: string;
    disabled?: boolean;
    subtitle?: string;
}

interface DropdownProps {
    options: DropdownOption[];
    value?: string;
    placeholder?: string;
    onChange?: (value: string) => void;
    className?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    'aria-label'?: string;
}

// Custom chevron down icon to avoid external dependency
const ChevronDownIcon = ({ className }: { className?: string }) => (
    <svg
        className={className}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
        />
    </svg>
);

export const Dropdown: React.FC<DropdownProps> = ({
    options,
    value,
    placeholder = 'Click to select',
    onChange,
    className = '',
    open,
    onOpenChange,
    'aria-label': ariaLabel
}) => {
    const classes = useStyles();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value || '');
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    // Use external open state if provided, otherwise use internal state
    const isOpen = open !== undefined ? open : internalIsOpen;
    const setIsOpen = useCallback((newOpen: boolean) => {
        if (open !== undefined) {
            onOpenChange?.(newOpen);
        } else {
            setInternalIsOpen(newOpen);
        }
    }, [open, onOpenChange]);

    const selectedOption = options.find((option) => option.value === selectedValue);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [setIsOpen]);

    useEffect(() => {
        setSelectedValue(value || '');
    }, [value]);

    const handleSelect = (option: DropdownOption) => {
        if (option.disabled) return;

        setSelectedValue(option.value);
        setIsOpen(false);
        onChange?.(option.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent, option?: DropdownOption) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (option) {
                handleSelect(option);
            } else {
                setIsOpen(!isOpen);
            }
        }
        if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div className={`${classes.dropdownContainer} ${className}`} ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                type="button"
                className={classes.dropdownTrigger}
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={(e) => handleKeyDown(e)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={ariaLabel}
            >
                <span className={`${classes.dropdownText} ${selectedValue ? '' : 'placeholder'}`}>
                    {selectedOption?.label || placeholder}
                </span>
                <ChevronDownIcon className={`${classes.chevronIcon} ${isOpen ? 'open' : ''}`} />
            </button>


            {/* Dropdown Menu */}
            {isOpen && (
                <div className={classes.dropdownMenu}>
                    {options.map((option, index) => (
                        <div
                            key={index}
                            className={`${classes.menuItem} ${
                                option.disabled ? 'disabled' : ''
                            } ${selectedValue === option.value ? 'selected' : ''}`}
                            onClick={() => handleSelect(option)}
                            onKeyDown={(e) => handleKeyDown(e, option)}
                            tabIndex={option.disabled ? -1 : 0}
                            role="option"
                            aria-selected={selectedValue === option.value}
                            aria-disabled={option.disabled}
                        >
                            <div>
                                <div>{option.label}</div>
                                {option.disabled && option.subtitle && (
                                    <div className={classes.menuItemSubtitle}>
                                        {option.subtitle}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
